import { useCallback, useEffect, useId, useRef, type JSX, type KeyboardEvent } from 'react';
import { SPECIES_LIST, SPECIES_META, type DuckColor, type DuckSpecies } from '@duck/contracts';
import { Application, type Ticker } from 'pixi.js';
import { Critter } from '../office/render/critter';

/**
 * The CHARACTER PICKER (ADR-0024) — choose your ANIMAL before you walk in, paired with the existing
 * colour-tint {@link DuckPicker}. Same M0 "First Breath" feel as the duck pick: choosing is INSTANT and
 * freely reversible (no confirm), and the live {@link CritterPreview} updates on the spot — "ooh, try
 * them all". Accessibility is first-class (an ARIA radiogroup, full keyboard nav, dual-coded selection).
 *
 * The preview is WYSIWYG: it renders the REAL procedural {@link Critter} (the very same class the office
 * draws), so the animal you see IS the animal you'll be — no second, drifting illustration to maintain.
 * Pixi is already loaded on these screens for the {@link LivingBackdrop}, so this adds no bundle cost.
 */

// ── The accessible species grid ──────────────────────────────────────────────────────────────────────

export function SpeciesPicker({
  value,
  onChange,
}: {
  value: DuckSpecies;
  onChange: (species: DuckSpecies) => void;
}): JSX.Element {
  const groupLabelId = useId();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  // Roving-focus arrow-key navigation across the radiogroup (left/right within a row, up/down across the
  // two rows of five). Selecting also moves focus so the keyboard + pointer paths feel identical.
  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
      const last = SPECIES_LIST.length - 1;
      const COLS = 5;
      let next = index;
      switch (e.key) {
        case 'ArrowRight':
          next = index === last ? 0 : index + 1;
          break;
        case 'ArrowLeft':
          next = index === 0 ? last : index - 1;
          break;
        case 'ArrowDown':
          next = index + COLS > last ? index % COLS : index + COLS;
          break;
        case 'ArrowUp':
          next = index - COLS < 0 ? Math.min(last, index + COLS) : index - COLS;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = last;
          break;
        default:
          return; // let Space/Enter activate the focused radio natively
      }
      e.preventDefault();
      onChange(SPECIES_LIST[next]!.id);
      refs.current[next]?.focus();
    },
    [onChange],
  );

  const selected = SPECIES_META[value];

  return (
    <div className="mb-4">
      <span id={groupLabelId} className="mb-1.5 block text-sm font-medium text-white/70">
        Choose your character
      </span>
      <div role="radiogroup" aria-labelledby={groupLabelId} className="grid grid-cols-5 gap-1.5">
        {SPECIES_LIST.map((meta, index) => {
          const isSel = value === meta.id;
          return (
            <button
              key={meta.id}
              ref={(el) => {
                refs.current[index] = el;
              }}
              type="button"
              role="radio"
              aria-checked={isSel}
              aria-label={meta.label}
              title={meta.label}
              tabIndex={isSel ? 0 : -1}
              onClick={() => onChange(meta.id)}
              onKeyDown={(e) => onKeyDown(e, index)}
              className={`relative flex h-11 items-center justify-center rounded-xl border-2 text-2xl leading-none outline-none transition focus-visible:ring-2 focus-visible:ring-amber-300/70 ${
                isSel
                  ? 'scale-105 border-amber-300 bg-amber-300/10'
                  : 'border-white/15 bg-black/20 hover:border-white/40'
              }`}
            >
              <span aria-hidden="true">{meta.emoji}</span>
              {/* Dual-code selection beyond the emoji/ring: a ✓ on the chosen animal. */}
              {isSel && (
                <span
                  aria-hidden="true"
                  className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-300 text-[10px] font-black leading-none text-slate-900 shadow"
                >
                  ✓
                </span>
              )}
            </button>
          );
        })}
      </div>
      {/* Dual-coded TEXT read-out of the current animal + its character note (never glyph-only). */}
      <p aria-live="polite" className="mt-2 text-xs text-white/60">
        <span className="font-semibold text-white/85">{selected.label}</span> — {selected.blurb}
      </p>
    </div>
  );
}

// ── The live WYSIWYG preview (the real procedural Critter) ────────────────────────────────────────────

interface PreviewHandle {
  update(species: DuckSpecies, color: DuckColor): void;
  destroy(): void;
}

/**
 * Mount a tiny, transparent Pixi stage into `container` showing ONE {@link Critter} at the chosen
 * species + tint, with the same gentle idle breathing it has in the world. Mirrors the proven
 * {@link mountLivingBackdrop} lifecycle (async v8 init, graceful null on no-WebGL, `isConnected` guard,
 * full teardown). Reduced-motion is honoured inside `Critter.setPose`, so the preview goes still too.
 */
async function mountCritterPreview(
  container: HTMLElement,
  initial: { species: DuckSpecies; color: DuckColor },
): Promise<PreviewHandle | null> {
  const app = new Application();
  let destroyed = false;

  try {
    await app.init({
      resizeTo: container,
      antialias: true,
      backgroundAlpha: 0,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
    });
  } catch {
    app.destroy(true);
    return null;
  }
  if (!container.isConnected) {
    app.destroy(true);
    return null;
  }
  container.appendChild(app.canvas);
  // Overlay the canvas exactly on the container so it sits ON TOP of the centered fallback glyph (which
  // is only meant to show if WebGL is unavailable), rather than beside it in the flex flow.
  app.canvas.style.position = 'absolute';
  app.canvas.style.top = '0';
  app.canvas.style.left = '0';

  // The critter art is authored ~70px tall around the origin; scale it up to fill the little card.
  const SCALE = 1.55;
  let species = initial.species;
  let color = initial.color;
  let critter = new Critter(species, color, '', false);
  critter.root.scale.set(SCALE);
  app.stage.addChild(critter.root);

  // Centre point (the critter's visual middle sits a touch above its feet, so nudge down a little).
  const cx = (): number => app.screen.width / 2;
  const cy = (): number => app.screen.height / 2 + 6 * SCALE;

  const tick = (ticker: Ticker): void => {
    if (destroyed) return;
    critter.setPose(cx(), cy(), 0, false, 'idle', ticker.deltaMS);
  };
  app.ticker.add(tick);

  // A decorative preview must NEVER crash the entry form, even if a Pixi teardown internal throws.
  const safeDestroy = (c: Critter): void => {
    try {
      c.destroy();
    } catch {
      /* non-fatal: a Pixi v8 texture-GC quirk on teardown — the form is unaffected. */
    }
  };

  return {
    update(s: DuckSpecies, c: DuckColor): void {
      if (destroyed || (s === species && c === color)) return;
      species = s;
      color = c;
      app.stage.removeChild(critter.root);
      safeDestroy(critter);
      critter = new Critter(species, color, '', false);
      critter.root.scale.set(SCALE);
      app.stage.addChild(critter.root);
    },
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      app.ticker.remove(tick);
      safeDestroy(critter);
      app.destroy(true, { children: true });
    },
  };
}

/**
 * Live, instant preview of the chosen critter. Sits at the top of the entry form so a species/colour
 * click is felt right away — the animal you'll BE. The species emoji shows as a graceful fallback behind
 * the canvas (covered once Pixi paints; the only thing visible if WebGL is unavailable).
 */
export function CritterPreview({
  species,
  color,
}: {
  species: DuckSpecies;
  color: DuckColor;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const handleRef = useRef<PreviewHandle | null>(null);
  // Always-current selection, so the ASYNC Pixi mount can catch up to whatever the user picked WHILE
  // `app.init()` was still resolving (WebGL init is tens of ms). Without this, a fast click during init
  // is lost — the update effect below no-ops against a still-null handle, and the preview would stay
  // stuck on the initial animal while a different one is selected.
  const latest = useRef({ species, color });
  latest.current = { species, color };

  // Mount once; the Pixi app + Critter live for the form's lifetime and are updated in place on change.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let cancelled = false;

    void mountCritterPreview(el, latest.current).then((h) => {
      if (cancelled) {
        h?.destroy();
        return;
      }
      handleRef.current = h;
      // Catch up to the CURRENT selection (a cheap no-op if nothing changed during init).
      h?.update(latest.current.species, latest.current.color);
    });

    return () => {
      cancelled = true;
      handleRef.current?.destroy();
      handleRef.current = null;
    };
    // Mount-only (deps []): the effect reads only refs (`latest`/`handleRef`) + the stable mount fn, so
    // there are no reactive deps to track; prop changes are pushed via the effect below (never remount Pixi).
  }, []);

  // Push species/colour changes into the already-mounted preview.
  useEffect(() => {
    handleRef.current?.update(species, color);
  }, [species, color]);

  return (
    <div
      ref={ref}
      data-critter-preview={species}
      // Fluid square preview: scales with the viewport so it never dominates a short tablet (portrait
      // or landscape) yet stays generous on big desktops. clamp(min, fluid, max) keeps the aspect 1:1.
      style={{ width: 'clamp(5rem, 18vh, 7rem)', height: 'clamp(5rem, 18vh, 7rem)' }}
      className="relative mx-auto mb-4"
      role="img"
      aria-label={`Preview of your ${SPECIES_META[species].label}`}
    >
      {/* Pixi paints the live critter into this box via the mount effect above; no DOM children. */}
    </div>
  );
}
